/**
 * Shamelessly inspired by https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/util/console.ts
 */

// Export noop for when in production mode
export let deprecate = () => {}
export let devConsole = {
  log: () => {},
  warn: () => {},
  error: () => {},
}

/**
 * Prevent deprecation notices being added in production builds and Jest test runs
 *
 * Jest sets NODE_ENV=test by default.
 */
if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
  /**
   * @param {string} message the message to be output
   * @param {object} vm the component's vm
   * @param {object} parent the component's parent
   * @returns {string|undefined} the created message
   */
  const createMessage = (message, vm, parent) => {
    if (parent) {
      vm = {
        _isVue: true,
        $parent: parent,
        $options: vm,
      }
    }

    if (vm) {
      // Only show each message once per instance
      vm.$_alreadyWarned = vm.$_alreadyWarned || []
      if (vm.$_alreadyWarned.includes(message)) return
      vm.$_alreadyWarned.push(message)
    }

    return `[ManyPatterns] ${message}` + (vm ? generateComponentTrace(vm) : '')
  }

  /**
   * @param {string} message the message to be output
   * @param {object} vm the component's vm
   * @param {object} parent the component's parent
   */
  const consoleWarn = (message, vm, parent) => {
    const newMessage = createMessage(message, vm, parent)
    newMessage != null && console.warn(newMessage)
  }

  /**
   * Shamelessly stolen from https://github.com/vuejs/vue/blob/dev/src/core/util/debug.js
   */

  const classifyRE = /(?:^|[-_])(\w)/g
  const classify = (str) =>
    str.replace(classifyRE, (c) => c.toUpperCase()).replace(/[-_]/g, '')

  const formatComponentName = (vm, includeFile) => {
    if (vm.$root === vm) {
      return '<Root>'
    }
    const options =
      typeof vm === 'function' && vm.cid != null
        ? vm.options
        : vm._isVue
          ? vm.$options || vm.constructor.options
          : vm
    let name = options.name || options._componentTag
    const file = options.__file
    if (!name && file) {
      const match = file.match(/([^/\\]+)\.vue$/)
      name = match && match[1]
    }

    return (
      (name ? `<${classify(name)}>` : `<Anonymous>`) +
      (file && includeFile !== false ? ` at ${file}` : '')
    )
  }

  const repeat = (str, n) => {
    let res = ''
    while (n) {
      if (n % 2 === 1) res += str
      if (n > 1) str += str
      n >>= 1
    }
    return res
  }

  const generateComponentTrace = (vm) => {
    if (vm._isVue && vm.$parent) {
      const tree = []
      let currentRecursiveSequence = 0
      while (vm) {
        if (tree.length > 0) {
          const last = tree[tree.length - 1]
          if (last.constructor === vm.constructor) {
            currentRecursiveSequence++
            vm = vm.$parent
            continue
          } else if (currentRecursiveSequence > 0) {
            tree[tree.length - 1] = [last, currentRecursiveSequence]
            currentRecursiveSequence = 0
          }
        }
        tree.push(vm)
        vm = vm.$parent
      }
      return (
        '\n\nfound in\n\n' +
        tree
          .map(
            (vm, i) =>
              `${i === 0 ? '---> ' : repeat(' ', 5 + i * 2)}${
                Array.isArray(vm)
                  ? `${formatComponentName(vm[0])}... (${
                      vm[1]
                    } recursive calls)`
                  : formatComponentName(vm)
              }`
          )
          .join('\n')
      )
    } else {
      return `\n\n(found in ${formatComponentName(vm)})`
    }
  }

  /**
   * Outputs a deprecation warning to the console
   *
   * Can be used for components and features in components
   * @param {object} props all the deprecate function props
   * @param {string} props.name the compoent name
   * @param {string} [props.prop] the deprecated prop
   * @param {string} [props.replacement] name of the replacement of the component/feature being deprecated
   * @param {object} [props.vm] the vm of the component being deprecated or the component with the deprecated feature
   * @param {object} [props.parent] the parent of the deprecated component/feature
   * @param {object} [props.removalDate] the date the deprecated item will be removed (usually 3 months from deprecation)
   * @param {string} props.ticket clickup url for the ticket to remove from target apps. Must contain team id and custom task id.
   */
  deprecate = ({
    name,
    prop,
    replacement,
    vm,
    parent,
    removalDate,
    ticket,
  }) => {
    if (!ticket) {
      console.error(`Deprecation message for ${name} must have a ticket url`)
    }

    let message = `[DEPRECATED] ${name}${
      prop ? `'s "${prop}" prop` : ''
    } is deprecated and will be removed ${
      removalDate ? `on ${removalDate}.` : 'in a later release.'
    } ${ticket}`

    if (replacement) {
      message += ` Use "${replacement}" instead.`
    }

    consoleWarn(message, vm, parent)
  }

  /**
   * Console methods that are only output in dev mode
   */
  devConsole = console
}
